/**
 * Personic v3.2 Persona Hooks
 *
 * React Query hooks for persona management:
 * - usePersonas: List personas with filtering
 * - usePersona: Get single persona by ID
 * - usePersonaConfiguration: Get generation configuration
 * - useUpdatePersonaConfiguration: Update configuration
 * - useGeneratePersona: Generate persona for a profile
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export type GenerationMode = 'full' | 'partial' | 'minimal'
export type SensitivityMode = 'full' | 'partial' | 'minimal'

export interface PersonaSection {
  summary?: string
  included?: boolean
  note?: string
  [key: string]: unknown
}

export interface CommunicationBriefing {
  summary: string
  formality: string
  verbosity: string
  directness: string
  technical_depth: string
  emotional_expressiveness?: string
  humor_appreciation?: string
}

export interface PersonalityContext {
  included: boolean
  note?: string
  openness?: string
  conscientiousness?: string
  extraversion?: string
  agreeableness?: string
  neuroticism?: string
}

export interface EmotionalContext {
  included: boolean
  note?: string
  baseline_tone?: string
  stress_response?: string
  trust_trajectory?: string
}

export interface BehavioralContext {
  included: boolean
  collaboration_style?: string
  feedback_receptivity?: string
  decision_pattern?: string
  learning_style?: string
}

export interface ConfidenceContext {
  overall_confidence: string
  profile_maturity: string
  observation_count: number
  recommendation: string
  high_confidence_signals?: string[]
  low_confidence_signals?: string[]
}

export interface Persona {
  persona_id: string
  version: number
  source_profile_id: number
  product_context: string
  tenant_id?: string
  generated_at: string
  sensitivity_mode: string
  communication_briefing: CommunicationBriefing
  personality_context: PersonalityContext
  emotional_context: EmotionalContext
  behavioral_context?: BehavioralContext
  adaptation_hints: string[]
  current_state_context?: {
    included: boolean
    energy_level?: string
    stress_level?: string
    mood?: string
    is_temporary?: boolean
  }
  confidence_context: ConfidenceContext
  compliance_notice?: string
  engine_algorithm_version?: string
  engine_confidence_score?: number
  framework_config?: Record<string, unknown>
  is_current?: boolean
  is_expired?: boolean
  last_used?: string
  use_count?: number
}

export interface PersonaReference {
  persona_id: string
  product_context: string
  tenant_id?: string
  current_version: number
  generated_at: string
  last_used?: string
  source_profile_maturity: string
  sensitivity_mode: string
}

export interface PersonaListResponse {
  profile_id: number
  canonical_id: string
  personas: PersonaReference[]
  total_count: number
}

export interface PersonaConfiguration {
  id?: string
  tenant_id: string
  default_generation_mode: GenerationMode
  sensitivity_mode: SensitivityMode
  refresh_on_profile_change: boolean
  ttl_hours: number
  auto_generate: boolean
  created_at?: string
  updated_at?: string
}

export interface PersonaFilters {
  profile_id?: string
  generation_mode?: GenerationMode
  product_context?: string
  search?: string
  page?: number
  limit?: number
}

export interface PaginatedPersonas {
  personas: Persona[]
  total: number
  page: number
  limit: number
  has_more: boolean
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  personas: ['personas'],
  persona: (id: string) => ['persona', id],
  configuration: ['persona-configuration'],
  personasByProfile: (profileId: string) => ['personas', 'profile', profileId],
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * List personas with filtering and pagination
 */
export function usePersonas(filters?: PersonaFilters) {
  return useQuery({
    queryKey: [...QUERY_KEYS.personas, filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.profile_id) params.append('profile_id', filters.profile_id)
      if (filters?.generation_mode) params.append('generation_mode', filters.generation_mode)
      if (filters?.product_context) params.append('product_context', filters.product_context)
      if (filters?.search) params.append('search', filters.search)
      if (filters?.page) params.append('page', String(filters.page))
      if (filters?.limit) params.append('limit', String(filters.limit))

      const { data } = await api.get<PaginatedPersonas>(`/personas?${params.toString()}`)
      return data
    },
  })
}

/**
 * Get a single persona by ID
 */
export function usePersona(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.persona(id),
    queryFn: async () => {
      const { data } = await api.get<Persona>(`/personas/${id}`)
      return data
    },
    enabled: !!id,
  })
}

/**
 * List personas for a specific profile
 */
export function useProfilePersonas(profileId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.personasByProfile(profileId),
    queryFn: async () => {
      const { data } = await api.get<PersonaListResponse>(
        `/api/mindmetal/v3/profile/${profileId}/personas`
      )
      return data
    },
    enabled: !!profileId,
  })
}

/**
 * Get tenant persona configuration
 */
export function usePersonaConfiguration() {
  return useQuery({
    queryKey: QUERY_KEYS.configuration,
    queryFn: async () => {
      const { data } = await api.get<PersonaConfiguration>('/personas/configuration')
      return data
    },
  })
}

/**
 * Update tenant persona configuration
 */
export function useUpdatePersonaConfiguration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (config: Partial<PersonaConfiguration>) => {
      const { data } = await api.put<PersonaConfiguration>('/personas/configuration', config)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.configuration })
    },
  })
}

/**
 * Generate a persona for a profile
 */
export function useGeneratePersona() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      profileId,
      productContext,
      sensitivityMode,
      includeCurrentState,
    }: {
      profileId: string
      productContext: string
      sensitivityMode?: SensitivityMode
      includeCurrentState?: boolean
    }) => {
      const { data } = await api.post<{ success: boolean; persona: Persona }>(
        `/api/mindmetal/v3/profile/${profileId}/persona/generate`,
        {
          product_context: productContext,
          sensitivity_mode: sensitivityMode,
          include_current_state: includeCurrentState,
        }
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.personas })
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.personasByProfile(variables.profileId),
      })
    },
  })
}

/**
 * Delete a persona
 */
export function useDeletePersona() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/personas/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.personas })
    },
  })
}
